import React, { useState, useEffect } from 'react';
import { useFirebase } from '../contexts/FirebaseContext';
import { useWordCount } from '../context/WordCountContext';
import { useWordContext } from '../context/WordContext';
import { useNavigate } from 'react-router-dom';
import logger from '../utils/logger';
import apiClient from '../utils/apiClient';
import chatMemoryManager from '../utils/chatMemoryManager';
import Pricing from './Pricing';
import '../styles/Profile.css';

// Reusable confirmation modal component with checkboxes for selecting languages.
const ConfirmModal = ({ title, message, onCancel, onConfirm, showLanguageSelect = false }) => {
    const { supportedLanguages } = useWordCount();
    const [selectedLanguages, setSelectedLanguages] = useState([]);
    const [deleteConfirmation, setDeleteConfirmation] = useState('');
    const isDeleteAccount = title === 'Delete Account';

    const handleCheckboxChange = (e) => {
        const { value, checked } = e.target;
        if (checked) {
            setSelectedLanguages(prev => [...prev, value]);
        } else {
            setSelectedLanguages(prev => prev.filter(lang => lang !== value));
        }
    };

    return (
        <div className="profile-modal-overlay">
            <div className="profile-modal-content">
                <h3 className="profile-modal-title">{title}</h3>
                <p className="profile-modal-message">{message}</p>
                
                {showLanguageSelect && (
                    <div style={{ textAlign: 'left', margin: '10px 0' }}>
                        <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>Choose language(s) to clear:</p>
                        {supportedLanguages.map(lang => (
                            <div key={lang}>
                                <label>
                                    <input
                                        type="checkbox"
                                        value={lang}
                                        onChange={handleCheckboxChange}
                                    />{' '}
                                    {lang}
                                </label>
                            </div>
                        ))}
                    </div>
                )}
                
                {isDeleteAccount && (
                    <div style={{ margin: '15px 0' }}>
                        <p style={{ fontWeight: 'bold', marginBottom: '5px', textAlign: 'left' }}>Type "delete" to confirm:</p>
                        <input
                            type="text"
                            value={deleteConfirmation}
                            onChange={(e) => setDeleteConfirmation(e.target.value)}
                            className="profile-password-input"
                            placeholder="delete"
                        />
                    </div>
                )}
                
                <div className="profile-modal-buttons">
                    <button onClick={onCancel} className="profile-modal-cancel-button">
                        Cancel
                    </button>
                    <button 
                        onClick={() => onConfirm(isDeleteAccount ? deleteConfirmation : selectedLanguages)} 
                        className="profile-modal-confirm-button"
                        disabled={isDeleteAccount && deleteConfirmation !== 'delete'}
                    >
                        Confirm
                    </button>
                </div>
            </div>
        </div>
    );
};

const Profile = () => {
    const { 
        user, 
        getToken, 
        isAuthenticated, 
        logOut, 
        updateUserPassword, 
        deleteUserAccount, 
        userPlan, 
        planExpiresAt,
        pendingDowngradeToPlan,
        pendingDowngradeAt, 
        createBillingPortalSession, 
        cancelSubscription 
    } = useFirebase();
    const { 
        wordCounts, 
        sentenceCounts, 
        loading: statsLoading, 
        refreshCounts, 
        lastRefresh,
        getJapaneseWordCount,
        getKanjiCount 
    } = useWordCount();
    const { refreshSavedWords, fetchStatus, supportedLanguages } = useWordContext();
    const navigate = useNavigate();
    
    const [profile, setProfile] = useState({
        username: '',
        email: '',
    });
    const [message, setMessage] = useState('');
    const [saved, setSaved] = useState(false);
    const [historyMessage, setHistoryMessage] = useState('');
    const [showClearHistoryConfirm, setShowClearHistoryConfirm] = useState(false);
    const [showDeleteAccountConfirm, setShowDeleteAccountConfirm] = useState(false);
    const [showPasswordFields, setShowPasswordFields] = useState(false);
    const [showMembershipDetails, setShowMembershipDetails] = useState(false);
    const [showCancelMembershipConfirm, setShowCancelMembershipConfirm] = useState(false);
    const [passwordData, setPasswordData] = useState({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [passwordMessage, setPasswordMessage] = useState('');
    const [passwordSaved, setPasswordSaved] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [clearedLanguages, setClearedLanguages] = useState([]);
    const [fieldErrors, setFieldErrors] = useState({
        username: '',
        email: ''
    });
    const [showClearMemoryConfirm, setShowClearMemoryConfirm] = useState(false);
    const [memoryMessage, setMemoryMessage] = useState('');
    const [cancelConfirmation, setCancelConfirmation] = useState('');
    const [membershipMessage, setMembershipMessage] = useState('');
    const [showPricingOverlay, setShowPricingOverlay] = useState(false);

    // Refresh data when component mounts
    useEffect(() => {
        const refreshAllData = async () => {
            try {
                if (isRefreshing) return; // Prevent multiple simultaneous refreshes
                
                setIsRefreshing(true);
                logger.debug('Profile - Refreshing all data on mount');
                
                // First fetch user profile
                if (user) {
                    logger.debug('Profile - Fetching user profile');
                    await fetchProfile();
                }
                
                // Force a complete refresh of all word data
                logger.debug('Profile - Refreshing word data for all languages');
                const wordResult = await refreshSavedWords();
                
                if (!wordResult.success) {
                    logger.warn('Profile - Word refresh returned error:', wordResult.error);
                }
                
                // Let the event system handle updating counts
                logger.debug('Profile - Word data refresh complete, counts will update automatically');
                
                // Reset cleared languages on full refresh
                setClearedLanguages([]);
                
                setIsRefreshing(false);
                setDataLoaded(true);
                logger.debug('Profile - Data refresh complete');
            } catch (error) {
                logger.error('Profile - Error refreshing data on mount:', error);
                setIsRefreshing(false);
                // Still mark as loaded even if there was an error
                setDataLoaded(true);
            }
        };
        
        // Only run once on mount
        refreshAllData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty dependency array

    // Monitor fetch status
    useEffect(() => {
        if (fetchStatus.inProgress) {
            setIsRefreshing(true);
        } else if (fetchStatus.lastCompleted) {
            setIsRefreshing(false);
        }
    }, [fetchStatus]);

    // Set profile data from Firebase user if available
    useEffect(() => {
        if (user) {
            logger.debug('Profile - Setting profile from Firebase user data:', user);
            // Set email from Firebase user
            setProfile(prev => ({
                ...prev,
                email: user.email || '',
                // Set username from Firebase display name or name
                username: user.displayName || user.name || '',
            }));
        }
    }, [user]);

    // Fetch the profile data from backend
    const fetchProfile = async () => {
        try {
            const token = await getToken();
            const response = await apiClient.get('/api/profile');
            
            if (response.ok) {
                const data = await response.json();
                const { userId, ...rest } = data;
                
                setProfile(prev => ({
                    ...prev,
                    ...rest
                }));
                
                // Store the username in localStorage for use across the app
                if (rest.username) {
                    localStorage.setItem('username', rest.username);
                }
                
                localStorage.setItem('userProfile', JSON.stringify(rest));
                logger.debug('Profile - Successfully fetched profile data:', rest);
            } else {
                throw new Error(`Failed to fetch profile: ${response.status}`);
            }
        } catch (error) {
            logger.error("Profile - Error fetching profile:", error);
            // Still try to use Firebase user data if backend fails
            if (user) {
                const updatedProfile = {
                    ...profile,
                    email: user.email || profile.email || '',
                    username: user.displayName || profile.username || '',
                };
                
                setProfile(updatedProfile);
                
                // Even with fallback, store the username
                if (updatedProfile.username) {
                    localStorage.setItem('username', updatedProfile.username);
                }
            }
            
            // Try to use cached profile data as fallback
            const storedProfile = localStorage.getItem('userProfile');
            if (storedProfile) {
                try {
                    const parsed = JSON.parse(storedProfile);
                    setProfile(prev => ({
                        ...prev,
                        ...parsed
                    }));
                    logger.debug('Profile - Using cached profile data');
                } catch (err) {
                    logger.error("Profile - Error parsing stored profile:", err);
                }
            }
        }
    };

    // Handle input changes.
    const handleChange = (e) => {
        const { name, value } = e.target;
        setProfile(prev => ({ ...prev, [name]: value }));
    };

    // Handle form submission to update the profile.
    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage('');
        setSaved(false);
        setFieldErrors({ username: '', email: '' });
        
        // Show loading state
        setIsRefreshing(true);
        
        try {
            const token = await getToken();
            const result = await apiClient.put('/api/profile', profile);
            
            // apiClient.put() returns parsed JSON (not a Response object)
            // Success response: { userId, username, email, plan, planExpiresAt }
            // Error response: { field, error } or { error }
            if (result && !result.error) {
                // Success - result contains the user data directly
                setProfile(prev => ({
                    ...prev,
                    ...result
                }));
                
                // Store the username in localStorage for use across the app
                if (result.username) {
                    localStorage.setItem('username', result.username);
                }
                
                localStorage.setItem('userProfile', JSON.stringify(result));
                setSaved(true);
                
                // Automatically hide success message after 5 seconds
                setTimeout(() => {
                    setSaved(false);
                }, 5000);
                
                logger.debug('Profile - Profile updated successfully');
            } else {
                // Check if the error is field-specific
                if (result && result.field) {
                    setFieldErrors(prev => ({
                        ...prev,
                        [result.field]: result.error
                    }));
                } else {
                    throw new Error((result && result.error) || 'Failed to update profile');
                }
            }
        } catch (error) {
            logger.error("Profile - Error updating profile:", error);
            setMessage('Failed to update profile. ' + error.message);
            setSaved(false);
        } finally {
            setIsRefreshing(false);
        }
    };

    // Handle password change form submission
    const handlePasswordChange = async (e) => {
        e.preventDefault();
        
        // Reset messages
        setPasswordMessage('');
        setPasswordSaved(false);
        
        // Validate passwords
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setPasswordMessage('New passwords do not match.');
            return;
        }
        
        if (passwordData.newPassword.length < 8) {
            setPasswordMessage('Password must be at least 8 characters long.');
            return;
        }
        
        try {
            logger.debug('Profile - Attempting to change password');
            
            // Use Firebase context method directly
            await updateUserPassword(passwordData.oldPassword, passwordData.newPassword);
            
            // Reset form
            setPasswordData({
                oldPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
            
            setPasswordSaved(true);
            setPasswordMessage('');
            logger.debug('Profile - Password changed successfully');
        } catch (error) {
            logger.error("Profile - Error changing password:", error);
            
            // Handle specific Firebase errors
            if (error.code === 'auth/wrong-password') {
                setPasswordMessage('Current password is incorrect.');
            } else if (error.code === 'auth/requires-recent-login') {
                setPasswordMessage('For security reasons, please log out and log back in before changing your password.');
            } else {
                setPasswordMessage(error.message || 'Failed to change password. Please try again later.');
            }
            
            setPasswordSaved(false);
        }
    };

    // Handle password input changes
    const handlePasswordInputChange = (e) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({ ...prev, [name]: value }));
    };

    // Clear History modal actions.
    const handleClearHistoryClick = () => {
        setShowClearHistoryConfirm(true);
    };

    const handleConfirmClearHistory = async (selectedLanguages) => {
        setShowClearHistoryConfirm(false);
        
        if (!selectedLanguages.length) {
            setHistoryMessage('No languages selected.');
            return;
        }
        
        logger.debug("Profile - Selected languages to clear:", selectedLanguages);
        try {
            // Immediately mark these languages as cleared for UI purposes
            setClearedLanguages(prev => [...prev, ...selectedLanguages]);
            
            const response = await apiClient.delete('/api/clearVocabulary', {
                data: { languages: selectedLanguages }
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                // If API call fails, restore the languages
                setClearedLanguages(prev => prev.filter(lang => !selectedLanguages.includes(lang)));
                throw new Error(errorData.message || `Failed to clear vocabulary: ${response.status}`);
            }
            
            // Refresh data after clearing
            await refreshSavedWords();
            await refreshCounts();
            
            // Remove deleted languages from clearedLanguages so stat boxes reappear
            // if they still have sentences reviewed or other non-vocab content
            setClearedLanguages(prev => prev.filter(lang => !selectedLanguages.includes(lang)));
            
            setHistoryMessage('Vocabulary deleted successfully for ' + selectedLanguages.join(', '));
            setMessage('');
            logger.debug('Profile - Successfully deleted vocabulary for languages:', selectedLanguages);
        } catch (error) {
            logger.error("Profile - Error deleting vocabulary:", error);
            setMessage('Failed to delete vocabulary: ' + error.message);
            setHistoryMessage('');
        }
    };

    const handleCancelClearHistory = () => {
        setShowClearHistoryConfirm(false);
    };

    // Delete Account modal actions
    const handleDeleteAccountClick = () => {
        setShowDeleteAccountConfirm(true);
    };

    const handleConfirmDeleteAccount = async (confirmation) => {
        setShowDeleteAccountConfirm(false);
        
        if (confirmation !== 'delete') {
            setMessage('You must type "delete" to confirm account deletion.');
            return;
        }
        
        try {
            logger.debug('Profile - Attempting to delete account');
            
            // Use Firebase context method directly - passing null since we no longer need password
            await deleteUserAccount(null);
            
            // Log the user out after account deletion
            logger.info('Profile - Account deleted successfully, logging out');
            
            // Redirect to home page
            window.location.href = '/';
        } catch (error) {
            logger.error("Profile - Error deleting account:", error);
            
            // Handle specific Firebase errors
            if (error.code === 'auth/requires-recent-login') {
                setMessage('For security reasons, please log out and log back in before deleting your account.');
            } else {
                setMessage(error.message || 'Failed to delete account. Please try again later.');
            }
        }
    };

    const handleCancelDeleteAccount = () => {
        setShowDeleteAccountConfirm(false);
    };

    // Function to toggle password change fields
    const togglePasswordFields = () => {
        setShowPasswordFields(!showPasswordFields);
        // Reset form and messages when toggling
        if (!showPasswordFields) {
            setPasswordData({
                oldPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
            setPasswordMessage('');
            setPasswordSaved(false);
        }
    };

    // Function to cancel password change
    const handleCancelPasswordChange = () => {
        setShowPasswordFields(false);
        setPasswordData({
            oldPassword: '',
            newPassword: '',
            confirmPassword: ''
        });
        setPasswordMessage('');
        setPasswordSaved(false);
    };

    // Toggle membership details display
    const toggleMembershipDetails = () => {
        setShowMembershipDetails(!showMembershipDetails);
    };
    
    // Handle opening the cancel membership confirmation modal
    const handleCancelMembershipClick = () => {
        setShowCancelMembershipConfirm(true);
    };
    
    // Handle confirmation of cancelling membership
    const handleConfirmCancelMembership = async (confirmation) => {
        if (confirmation !== 'cancel') {
            return;
        }
        
        try {
            const result = await cancelSubscription();
            
            if (result) {
                setMembershipMessage('Your membership has been cancelled successfully.');
            } else {
                setMembershipMessage('Failed to cancel membership. Please try again or contact support.');
            }
        } catch (error) {
            logger.error('Error cancelling membership:', error);
            setMembershipMessage('An error occurred while cancelling your membership.');
        }
        
        setShowCancelMembershipConfirm(false);
        setCancelConfirmation('');
    };
    
    // Handle cancelling the confirmation modal
    const handleCancelCancelMembership = () => {
        setShowCancelMembershipConfirm(false);
        setCancelConfirmation('');
    };
    
    // Handle upgrading or changing plan
    const handleChangePlan = () => {
        // All users now see the pricing overlay
        setShowPricingOverlay(true);
    };
    
    // Handle closing the pricing overlay
    const handleClosePricing = () => {
        setShowPricingOverlay(false);
    };

    // Calculate days remaining for trial users
    const calculateDaysRemaining = () => {
        if (!planExpiresAt) return 0;
        
        const now = new Date();
        const expiration = new Date(planExpiresAt);
        const diffTime = expiration - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        return diffDays > 0 ? diffDays : 0;
    };
    
    // Format the renewal date
    const formatRenewalDate = () => {
        if (!planExpiresAt) return '';
        
        return new Date(planExpiresAt).toLocaleDateString();
    };

    // Get array of languages with saved words or sentences, excluding cleared languages
    const languagesWithSavedContent = [...new Set([
        ...Object.keys(wordCounts || {}),
        ...Object.keys(sentenceCounts || {})
    ])].filter(language => 
        (wordCounts[language] > 0 || 
        (sentenceCounts[language] && sentenceCounts[language] > 0) || 
        (language === 'Japanese' && getKanjiCount() > 0)) && 
        !clearedLanguages.includes(language) &&
        !language.includes('_') // Filter out special keys like Japanese_words
    );

    // Function to manually refresh stats
    const handleRefreshStats = async () => {
        try {
            if (isRefreshing) return; // Prevent multiple simultaneous refreshes
            
            setIsRefreshing(true);
            logger.debug('Profile - Manually refreshing stats');
            
            // Reset cleared languages on manual refresh
            setClearedLanguages([]);
            
            // Use the full refresh cycle
            const wordResult = await refreshSavedWords();
            if (!wordResult.success) {
                logger.warn('Profile - Manual word refresh returned error:', wordResult.error);
            }
            
            // Explicitly refresh all counts including sentence counts
            await refreshCounts();
            
            // The event system will handle updating counts
            logger.debug('Profile - Manual refresh complete');
        } catch (error) {
            logger.error('Profile - Error during manual refresh:', error);
        } finally {
            // Don't set isRefreshing to false here - let the fetch status effect handle it
        }
    };

    // Clear Chat Memory modal actions
    const handleClearMemoryClick = () => {
        // No need for language selection, so show a simpler confirmation
        setShowClearMemoryConfirm(true);
    };

    const handleConfirmClearMemory = async () => {
        setShowClearMemoryConfirm(false);
        
        logger.debug("Profile - Clearing all chat memory");
        try {
            if (user && user.uid) {
                // With the new memory system, we just need to clear all memory at once
                // Use the async version of clearMemory
                await chatMemoryManager.clearMemory(user.uid);
                
                setMemoryMessage('Chat memory cleared successfully');
                logger.debug('Profile - Successfully cleared all chat memory');
            } else {
                throw new Error('User not authenticated');
            }
        } catch (error) {
            logger.error("Profile - Error clearing chat memory:", error);
            setMemoryMessage('Failed to clear chat memory: ' + error.message);
        }
    };

    const handleCancelClearMemory = () => {
        setShowClearMemoryConfirm(false);
    };

    // Membership confirmation modal
    const MembershipCancelModal = ({ onCancel, onConfirm }) => {
        return (
            <div className="profile-modal-overlay">
                <div className="profile-modal-content">
                    <h3 className="profile-modal-title">Cancel Membership</h3>
                    <p className="profile-modal-message">Are you sure you want to cancel your membership? You will lose access to premium features at the end of your billing period.</p>
                    
                    <div style={{ margin: '15px 0' }}>
                        <p style={{ fontWeight: 'bold', marginBottom: '5px', textAlign: 'left' }}>Type "cancel" to confirm:</p>
                        <input
                            type="text"
                            value={cancelConfirmation}
                            onChange={(e) => setCancelConfirmation(e.target.value)}
                            className="profile-password-input"
                            placeholder="cancel"
                        />
                    </div>
                    
                    <div className="profile-modal-buttons">
                        <button onClick={onCancel} className="profile-modal-cancel-button">
                            Go Back
                        </button>
                        <button 
                            onClick={() => onConfirm(cancelConfirmation)} 
                            className="profile-modal-confirm-button"
                            disabled={cancelConfirmation !== 'cancel'}
                        >
                            Confirm
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="profile-container">
            <style>
                {`
                input {
                    background-color: #f0f0f0;
                }
                input:focus {
                    background-color: #fff;
                }
                .global-save-button.clear-history {
                    background-color: rgb(151, 151, 151) !important;
                    display: inline-block !important;
                    margin: 0 !important;
                }
                .refresh-stats-button {
                    background: none;
                    border: none;
                    color: #68b7f3;
                    cursor: pointer;
                    font-size: 14px;
                    text-decoration: underline;
                    margin-left: 10px;
                    padding: 0;
                }
                .refresh-stats-button:disabled {
                    color: #999;
                    cursor: not-allowed;
                }
                .refresh-stats-icon {
                    display: inline-block;
                    margin-right: 4px;
                    animation: none;
                }
                .refresh-stats-icon.spinning {
                    animation: spin 1s linear infinite;
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                .profile-last-refresh {
                    font-size: 12px;
                    color: #777;
                    margin-top: 15px;
                    text-align: right;
                }
                .input-error {
                    border: 1px solid #e53935 !important;
                    background-color: #ffebee !important;
                }
                .field-error-message {
                    color: #e53935;
                    font-size: 12px;
                    margin-top: 4px;
                    margin-bottom: 4px;
                    text-align: left;
                }
                `}
            </style>

            <h1 className="page-title">Profile Settings</h1>

            {/* Profile Information Container */}
            <div className="profile-menu-container">
                <section className="profile-section">
                    <div className="profile-group">
                        <span className="global-menu-title">Username:</span>
                        <input
                            type="text"
                            name="username"
                            value={profile.username}
                            onChange={handleChange}
                            className={`profile-input ${fieldErrors.username ? 'input-error' : ''}`}
                            required
                        />
                        {fieldErrors.username && (
                            <div className="field-error-message">{fieldErrors.username}</div>
                        )}
                    </div>
                </section>

                <section className="profile-section">
                    <div className="profile-group">
                        <span className="global-menu-title">Email:</span>
                        <input
                            type="email"
                            name="email"
                            value={profile.email}
                            onChange={handleChange}
                            className={`profile-input ${fieldErrors.email ? 'input-error' : ''}`}
                            required
                        />
                        {fieldErrors.email && (
                            <div className="field-error-message">{fieldErrors.email}</div>
                        )}
                    </div>
                </section>

                {/* Password Management Section */}
                <section className="profile-section">
                    <div className="profile-group">
                        {!showPasswordFields ? (
                            <span
                                className="change-password-link"
                                onClick={togglePasswordFields}
                            >
                                Change Password
                            </span>
                        ) : (
                            <div className="profile-password-change-form">
                                <div className="profile-password-field">
                                    <span className="global-menu-title">Current Password:</span>
                                    <input
                                        type="password"
                                        name="oldPassword"
                                        value={passwordData.oldPassword}
                                        onChange={handlePasswordInputChange}
                                        className="profile-input"
                                        required
                                    />
                                </div>
                                
                                <div className="profile-password-field">
                                    <span className="global-menu-title">New Password:</span>
                                    <input
                                        type="password"
                                        name="newPassword"
                                        value={passwordData.newPassword}
                                        onChange={handlePasswordInputChange}
                                        className="profile-input"
                                        required
                                    />
                                </div>
                                
                                <div className="profile-password-field">
                                    <span className="global-menu-title">Confirm New Password:</span>
                                    <input
                                        type="password"
                                        name="confirmPassword"
                                        value={passwordData.confirmPassword}
                                        onChange={handlePasswordInputChange}
                                        className="profile-input"
                                        required
                                    />
                                </div>
                                
                                <div className="profile-password-buttons">
                                    <button 
                                        onClick={handlePasswordChange} 
                                        className="profile-password-update-button"
                                    >
                                        Update
                                    </button>
                                    <button 
                                        onClick={handleCancelPasswordChange} 
                                        className="profile-password-cancel-button"
                                    >
                                        Cancel
                                    </button>
                                </div>
                                
                                {passwordMessage && (
                                    <p className="profile-message" style={{marginTop: '10px'}}>{passwordMessage}</p>
                                )}
                                
                                {passwordSaved && (
                                    <p className="profile-saved-text" style={{marginTop: '10px'}}>Password updated successfully!</p>
                                )}
                            </div>
                        )}
                    </div>
                </section>

                {/* Membership Section */}
                <section className="profile-section">
                    <div className="profile-group">
                        {!showMembershipDetails ? (
                            <span
                                className="change-password-link"
                                onClick={toggleMembershipDetails}
                            >
                                Your Membership
                            </span>
                        ) : (
                            <div className="profile-password-change-form">
                                <div style={{ marginBottom: '10px', position: 'relative' }}>
                                    <span 
                                        style={{ 
                                            position: 'absolute', 
                                            right: '0', 
                                            top: '0', 
                                            color: '#77a7bf', 
                                            cursor: 'pointer',
                                            fontWeight: 'bold',
                                            padding: '4px 8px',
                                            fontSize: '16px',
                                            lineHeight: '16px',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            width: '16px',
                                            height: '16px'
                                        }} 
                                        onClick={toggleMembershipDetails}
                                    >
                                        ×
                                    </span>
                                    <span style={{ fontWeight: 'bold', color: 'white' }}>Your plan: </span>
                                    <span style={{ 
                                        textTransform: 'capitalize', 
                                        color: userPlan === 'trial' ? '#ff8080' : 
                                               userPlan === 'canceled' ? '#e74c3c' :
                                               userPlan === 'premium' || userPlan === 'pro' ? '#4caf50' : 'white'
                                    }}>
                                        {userPlan === 'canceled' ? 'Canceled' : userPlan}
                                    </span>
                                    {userPlan === 'trial' ? (
                                        <span style={{ color: 'white' }}> ({calculateDaysRemaining()} Days Remaining)</span>
                                    ) : userPlan === 'canceled' ? (
                                        <span style={{ color: '#ffcc80' }}> (Your subscription has ended)</span>
                                    ) : (
                                        <span style={{ color: 'white' }}> (renewal date: {formatRenewalDate()})</span>
                                    )}
                                    
                                    {pendingDowngradeToPlan && (
                                        <div style={{ marginTop: '5px', fontSize: '14px', color: '#ffcc80' }}>
                                            You will be downgraded to <span style={{ textTransform: 'capitalize' }}>{pendingDowngradeToPlan}</span> on {new Date(pendingDowngradeAt).toLocaleDateString()}. Until then, you'll keep all your current features.
                                        </div>
                                    )}
                                </div>
                                
                                <div className="profile-password-buttons" style={{ marginTop: '10px' }}>
                                    <button 
                                        onClick={handleChangePlan}
                                        className="profile-password-update-button"
                                    >
                                        {userPlan === 'trial' || userPlan === 'canceled' ? 'Upgrade' : 'Change Plan'}
                                    </button>
                                </div>
                                
                                {userPlan !== 'trial' && userPlan !== 'canceled' && (
                                    <div style={{ marginTop: '15px', textAlign: 'center' }}>
                                        <span 
                                            className="delete-account-link" 
                                            style={{ color: '#d9534f', textAlign: 'center' }}
                                            onClick={handleCancelMembershipClick}
                                        >
                                            Cancel Membership
                                        </span>
                                    </div>
                                )}
                                
                                {membershipMessage && (
                                    <p className="profile-message" style={{marginTop: '10px'}}>{membershipMessage}</p>
                                )}
                            </div>
                        )}
                    </div>
                </section>
            </div>

            {/* Stats Container */}
            <div className="profile-menu-container">
                <h2 className="profile-stats-header">
                    Your Stats
                    <button 
                        onClick={handleRefreshStats} 
                        className="refresh-stats-button"
                        disabled={isRefreshing}
                        title="Refresh your stats"
                    >
                        <span className={`refresh-stats-icon ${isRefreshing ? 'spinning' : ''}`}>⟳</span>
                        {isRefreshing ? 'Refreshing...' : 'Refresh'}
                    </button>
                </h2>
                
                {!dataLoaded ? (
                    <p className="profile-loading-text">Loading your stats...</p>
                ) : isRefreshing ? (
                    <p className="profile-loading-text">Refreshing your stats...</p>
                ) : statsLoading ? (
                    <p className="profile-loading-text">Processing your stats...</p>
                ) : languagesWithSavedContent.length > 0 ? (
                    <div>
                        {languagesWithSavedContent.map(language => (
                            <div key={language} className="profile-stats-item">
                                <h3 className="profile-language-title">{language}</h3>
                                <div className="profile-stats-row">
                                    <span>Sentences reviewed:</span>
                                    <span>{sentenceCounts[language] || 0}</span>
                                </div>
                                
                                {language === 'Japanese' ? (
                                    <>
                                        <div className="profile-stats-row">
                                            <span>Words saved:</span>
                                            <span>{getJapaneseWordCount()}</span>
                                        </div>
                                        <div className="profile-stats-row">
                                            <span>Kanji saved:</span>
                                            <span>{getKanjiCount()}</span>
                                        </div>
                                    </>
                                ) : (
                                    <div className="profile-stats-row">
                                        <span>Words saved:</span>
                                        <span>{wordCounts[language] || 0}</span>
                                    </div>
                                )}
                            </div>
                        ))}
                        {lastRefresh && (
                            <p className="profile-last-refresh">
                                Last updated: {new Date(lastRefresh).toLocaleTimeString()}
                            </p>
                        )}
                    </div>
                ) : (
                    <div>
                        <p className="profile-no-stats-text">You haven't saved any words yet.</p>
                        {lastRefresh && (
                            <p className="profile-last-refresh">
                                Last checked: {new Date(lastRefresh).toLocaleTimeString()}
                            </p>
                        )}
                    </div>
                )}
                
                <section className="profile-stats-controls">
                    <div className="profile-group">
                        <button
                            type="button"
                            onClick={handleClearHistoryClick}
                            className="global-save-button clear-history"
                        >
                            Delete Saved Vocabulary 
                        </button>
                        <p className="profile-tagline">
                            Select for which language(s) to delete your saved vocabulary.
                        </p>
                    </div>

                    <div className="profile-group">
                        <button
                            type="button"
                            onClick={handleClearMemoryClick}
                            className="global-save-button clear-history"
                        >
                            Clear Chat Memory
                        </button>
                        <p className="profile-tagline">
                            Clear Bojee's memory of all facts learned across all conversations.
                        </p>
                    </div>
                    
                    {/* Save Profile Button */}
                    <div className="profile-group" style={{ textAlign: 'center', marginTop: '20px' }}>
                        <button 
                            onClick={handleSubmit} 
                            className="profile-save-button"
                            disabled={isRefreshing}
                        >
                            {isRefreshing ? 'Saving...' : 'Save Profile'}
                        </button>
                        
                        {/* Messaging area - moved inside the container */}
                        {saved ? (
                            <p className="profile-saved-text">Profile updated successfully!</p>
                        ) : (
                            message && <p className="profile-message">{message}</p>
                        )}
                        
                        {historyMessage && <p className="profile-saved-text">{historyMessage}</p>}
                        {memoryMessage && <p className="profile-saved-text">{memoryMessage}</p>}
                        
                        {/* Delete Account Link */}
                        <span 
                            className="delete-account-link" 
                            onClick={handleDeleteAccountClick}
                        >
                            Delete Account
                        </span>
                    </div>
                </section>
            </div>

            {/* Modals */}
            {showClearHistoryConfirm && (
                <ConfirmModal
                    title="Delete Saved Vocabulary"
                    message="Are you sure you want to clear your saved vocabulary? This action cannot be undone."
                    onCancel={handleCancelClearHistory}
                    onConfirm={handleConfirmClearHistory}
                    showLanguageSelect={true}
                />
            )}
            
            {showDeleteAccountConfirm && (
                <ConfirmModal
                    title="Delete Account"
                    message="Are you sure you want to delete your account? This action cannot be undone and you will lose all your data."
                    onCancel={handleCancelDeleteAccount}
                    onConfirm={handleConfirmDeleteAccount}
                />
            )}
            
            {showCancelMembershipConfirm && (
                <MembershipCancelModal
                    onCancel={handleCancelCancelMembership}
                    onConfirm={handleConfirmCancelMembership}
                />
            )}
            
            {showClearMemoryConfirm && (
                <ConfirmModal
                    title="Clear Chat Memory"
                    message="Are you sure you want to clear all of Bojee's memory? This will remove all facts and information Bojee has learned about you across all conversations and languages. This cannot be undone."
                    onCancel={handleCancelClearMemory}
                    onConfirm={handleConfirmClearMemory}
                    showLanguageSelect={false}
                />
            )}
            
            {/* Pricing overlay */}
            {showPricingOverlay && <Pricing onClose={handleClosePricing} />}
        </div>
    );
};

export default Profile;
